import { useState, useEffect, useCallback } from 'react';
import { GameMode } from '@/lib/types/common/GameMode';
import { GameModeAdvancedSettings, DEFAULT_GAME_MODE_SETTINGS } from '@/lib/types/common/GameModeAdvancedSettings';

const STORAGE_KEY_PREFIX = 'eorguessr_gamemode_settings_';

type GameModeSettingsStorage = {
    [K in GameMode]?: Partial<GameModeAdvancedSettings>;
};

export function useGameModeSettings(gameMode: GameMode) {
    const [settings, setSettings] = useState<GameModeAdvancedSettings>(() => {
        // Initialize with default settings
        return DEFAULT_GAME_MODE_SETTINGS[gameMode];
    });

    // Load settings from localStorage on mount and when gameMode changes
    useEffect(() => {
        if (typeof window === 'undefined') return;

        try {
            const storageKey = `${STORAGE_KEY_PREFIX}${gameMode}`;
            const savedSettings = localStorage.getItem(storageKey);

            if (savedSettings) {
                const parsedSettings = JSON.parse(savedSettings);
                // Merge saved settings with defaults to ensure all required fields are present
                const defaultSettings = DEFAULT_GAME_MODE_SETTINGS[gameMode];
                const mergedSettings = {
                    ...defaultSettings,
                    ...parsedSettings,
                    gameMode, // Ensure gameMode is always correct
                } as GameModeAdvancedSettings;

                setSettings(mergedSettings);
                console.log(`📁 Loaded saved settings for ${gameMode}:`, mergedSettings);
            } else {
                // Use default settings if none saved
                setSettings(DEFAULT_GAME_MODE_SETTINGS[gameMode]);
                console.log(`📁 Using default settings for ${gameMode}:`, DEFAULT_GAME_MODE_SETTINGS[gameMode]);
            }
        } catch (error) {
            console.warn(`Failed to load settings for ${gameMode}:`, error);
            setSettings(DEFAULT_GAME_MODE_SETTINGS[gameMode]);
        }
    }, [gameMode]);

    // Save settings to localStorage
    const saveSettings = useCallback((newSettings: GameModeAdvancedSettings) => {
        if (typeof window === 'undefined') return;

        try {
            const storageKey = `${STORAGE_KEY_PREFIX}${gameMode}`;
            // Only save the customizable settings, not all properties
            const settingsToSave = {
                maxRounds: newSettings.maxRounds,
                timerSeconds: newSettings.timerSeconds,
                // Add mode-specific settings
                ...(newSettings.gameMode === GameMode.FIGHT && {
                    teamMaxHp: (newSettings as any).teamMaxHp,
                    numberOfTeams: (newSettings as any).numberOfTeams,
                    damageMultiplierScaling: (newSettings as any).damageMultiplierScaling,
                }),
                ...(newSettings.gameMode === GameMode.COOP && {
                    numberOfTeams: (newSettings as any).numberOfTeams,
                }),
                ...(newSettings.gameMode === GameMode.DAILY && {
                    dailySeed: (newSettings as any).dailySeed,
                }),
                ...(newSettings.gameMode === GameMode.FLASH && {
                    flashDuration: (newSettings as any).flashDuration,
                }),
                ...(newSettings.gameMode === GameMode.HAZARD && {
                    hazardsPerRound: (newSettings as any).hazardsPerRound,
                    enabledHazards: (newSettings as any).enabledHazards,
                }),
                ...(newSettings.gameMode === GameMode.CUSTOM && {
                    baseMode: (newSettings as any).baseMode,
                    teamMaxHp: (newSettings as any).teamMaxHp,
                    numberOfTeams: (newSettings as any).numberOfTeams,
                    damageMultiplierScaling: (newSettings as any).damageMultiplierScaling,
                    hasFlashMode: (newSettings as any).hasFlashMode,
                    flashDuration: (newSettings as any).flashDuration,
                    hasHazards: (newSettings as any).hasHazards,
                    hazardsPerRound: (newSettings as any).hazardsPerRound,
                    enabledHazards: (newSettings as any).enabledHazards,
                    compassDisabled: (newSettings as any).compassDisabled,
                }),
            };

            localStorage.setItem(storageKey, JSON.stringify(settingsToSave));
            setSettings(newSettings);
            console.log(`💾 Saved settings for ${gameMode}:`, settingsToSave);
        } catch (error) {
            console.warn(`Failed to save settings for ${gameMode}:`, error);
        }
    }, [gameMode]);

    // Update specific setting
    const updateSetting = useCallback(<K extends keyof GameModeAdvancedSettings>(
        key: K,
        value: GameModeAdvancedSettings[K]
    ) => {
        const newSettings = {
            ...settings,
            [key]: value,
        };
        saveSettings(newSettings);
    }, [settings, saveSettings]);

    // Reset to defaults
    const resetToDefaults = useCallback(() => {
        if (typeof window === 'undefined') return;

        try {
            const storageKey = `${STORAGE_KEY_PREFIX}${gameMode}`;
            localStorage.removeItem(storageKey);
            setSettings(DEFAULT_GAME_MODE_SETTINGS[gameMode]);
            console.log(`🔄 Reset settings for ${gameMode} to defaults`);
        } catch (error) {
            console.warn(`Failed to reset settings for ${gameMode}:`, error);
        }
    }, [gameMode]);

    // Get all saved settings for all modes (useful for debugging)
    const getAllSavedSettings = useCallback((): GameModeSettingsStorage => {
        if (typeof window === 'undefined') return {};

        const allSettings: GameModeSettingsStorage = {};

        Object.values(GameMode).forEach((mode) => {
            try {
                const storageKey = `${STORAGE_KEY_PREFIX}${mode}`;
                const savedSettings = localStorage.getItem(storageKey);
                if (savedSettings) {
                    allSettings[mode] = JSON.parse(savedSettings);
                }
            } catch (error) {
                console.warn(`Failed to load settings for ${mode}:`, error);
            }
        });

        return allSettings;
    }, []);

    return {
        settings,
        saveSettings,
        updateSetting,
        resetToDefaults,
        getAllSavedSettings,
    };
}

export default useGameModeSettings;